import React, { PureComponent, Fragment } from 'react';
import ReactDOM from 'react-dom';
import { graphql, withApollo } from 'react-apollo';
import isEqual from 'lodash/isEqual';
import isString from 'lodash/isString';
import makeCursor from 'legacy-src/utility/Cursor';
import ProseMirror, {
  docIsEmpty,
} from 'legacy/src/components/content/ProseMirror';
import { assignmentWithAssessment, saveFeedback } from 'legacy/src/queries';
import './styles.scss';
import PropTypes from 'prop-types';
import {
  clearDataFromLocalStorage,
  retrieveFromLocalStorage,
  saveToLocalStorage,
} from 'legacy-src/utility/LocalStorage';
import { isValidJSONString } from 'legacy-src/utility/utils';
import cn from 'classnames';
import Button from 'chameleon/ui-stack/ui/base/button';

const PERSIST_AFTER_LOGOUT_KEY = 'persistAfterLogout';
import ButtonWithIcon from 'chameleon/ui-stack/ui/buttons/button-with-icon';
import ConfirmDialog from 'chameleon/ui-stack/ui/modals/confirm-dialog';

class FeedbackButton extends PureComponent {
  constructor(props) {
    super(props);
    this.state = {
      isModalOpen: false,
      hasFeedback: this.props.text || false,
      text: this.props.text, // state.text would we object all the time, props.text is string
      oldFeedback: this.props.text || '',
      saved: false,
    };
  }

  setStatus = (hasFeedback) => {
    this.setState({
      hasFeedback,
    });
  };

  getDataSetModal = (saveToBrowserKey) => {
    const data = retrieveFromLocalStorage(saveToBrowserKey);
    if (data) this.setState({ text: data });
  };

  componentDidMount() {
    const { saveToBrowser, saveToBrowserKey } = this.props;

    if (saveToBrowser) {
      this.getDataSetModal(saveToBrowserKey);

      let persistAfterLogout = retrieveFromLocalStorage(
        PERSIST_AFTER_LOGOUT_KEY,
      );

      if (persistAfterLogout) {
        if (!persistAfterLogout.includes(saveToBrowserKey))
          persistAfterLogout.push(saveToBrowserKey);
      } else {
        persistAfterLogout = [saveToBrowserKey];
      }

      saveToLocalStorage(PERSIST_AFTER_LOGOUT_KEY, persistAfterLogout);
    }
  }

  componentDidUpdate(prevProps) {
    const {
      text,
      saveToBrowser,
      saveToBrowserKey,
      showFeedbackPanel,
      disabled,
      openByDefault,
    } = this.props;

    // set initial state to parsed prosemirrow json from the props
    if (text && prevProps.text !== text && !this.state.text) {
      this.updateText(text, true);
      this.setStatus(!docIsEmpty(text));
    }

    // if user has something un-saved in localStore make panel opened by default
    if (
      saveToBrowser &&
      saveToBrowserKey &&
      showFeedbackPanel &&
      prevProps.disabled !== disabled
    ) {
      openByDefault && this.toggleModal(true);

      const savedOnBrowser = retrieveFromLocalStorage(saveToBrowserKey);
      // check if saved feedback is actually different from API has
      // open panel even if user actually deletes all feedback and did not save it
      if (
        savedOnBrowser &&
        !isEqual(text && JSON.parse(text), savedOnBrowser)
      ) {
        this.toggleModal(true);
        this.moveFocusToFeedbackBox();
      }
    }
  }

  /**
   *
   * @param text {String}
   * @param updateOldFeedback {boolean}
   */
  updateText(text, updateOldFeedback = false) {
    const data = { text: JSON.parse(text) };
    if (updateOldFeedback) data.oldFeedback = text;

    this.setState(data);

    const { saveToBrowser, saveToBrowserKey } = this.props;
    this.state.isModalOpen &&
      saveToBrowser &&
      saveToLocalStorage(saveToBrowserKey, text);
  }

  sendFeedback = async (assignmentId, studentId, value) => {
    const {
      onSaved = () => {},
      showFeedbackPanel,
      saveToBrowserKey,
      saveFeedback: saveFeedbackProps,
    } = this.props;

    const content = isValidJSONString(value) ? value : JSON.stringify(value);

    saveFeedbackProps &&
      (await saveFeedbackProps({
        variables: {
          assignmentId,
          studentId,
          content: docIsEmpty(value) ? '{}' : content,
        },
      }));
    this.setStatus(!docIsEmpty(value));

    if (this.props.saveToParent) {
      this.props.saveToParent(content);
    }

    this.setState(
      {
        oldFeedback: content || '',
      },
      () => {
        onSaved();
        !showFeedbackPanel && this.toggleModal(false);
      },
    );

    clearDataFromLocalStorage(saveToBrowserKey);
    clearDataFromLocalStorage(PERSIST_AFTER_LOGOUT_KEY);
  };

  toggleModal = (isModalOpen, isCancel = false) => {
    const { oldFeedback, text } = this.state;
    if (!isModalOpen) {
      this.moveFocusToFeedbackButton();
    }
    this.setState({
      isModalOpen,
      text: isCancel ? oldFeedback : text,
    });

    const { saveToBrowser, saveToBrowserKey } = this.props;

    if (saveToBrowser && isCancel) {
      clearDataFromLocalStorage(saveToBrowserKey);
    }
  };

  moveFocusTo = (selector) => {
    setTimeout(() => {
      const element = document.querySelector(selector);
      element && element.focus();
    }, 200);
  };

  moveFocusToFeedbackBox = () => {
    this.moveFocusTo('.feedback-panel .top-section .header-text');
  };

  moveFocusToPanelTextEditor = () => {
    this.moveFocusTo('.feedback-panel [contenteditable]');
  };
  moveFocusToFeedbackButton = () => {
    this.moveFocusTo('.feedback-button');
  };

  moveFocusToFeedbackModal = () => {
    this.moveFocusTo('.feedback--modal .content');
  };

  openFeedbackPanel = (textCursor, hasChanges) => {
    const { assignmentId, studentId, studentName, headerText } = this.props;

    const { text, isModalOpen } = this.state;

    const scrollNodes = document.querySelectorAll(
      '[data-scoring-content] .content',
    );
    scrollNodes.forEach((node) => node.classList.add('--with-feedback-panel'));

    const panelHeaderText = headerText || `Provide feedback for ${studentName}`;

    isModalOpen && this.moveFocusToPanelTextEditor();

    return ReactDOM.createPortal(
      <div
        id="feedback-panel"
        aria-label={`Feedback panel is open. ${panelHeaderText}`}
        className="feedback-panel flow-root"
      >
        <div className="top-section">
          <div className="header-text">{panelHeaderText}</div>
          <ButtonWithIcon
            isNaked
            isIcon
            iconOrientation="right"
            className="hide-panel-button bg-white"
            onClick={() => {
              this.toggleModal(false, false);
              this.closeFeedbackPanel();
            }}
            accessibility={{
              'aria-label': 'Hide Feedback',
            }}
            icon="xmark"
          />
        </div>
        <div className="mb-5">
          <ProseMirror valueLink={textCursor} menu="feedback" />
        </div>
        <div className="desktop:float-right">
          <Button
            className="mr-4 mobile:hidden"
            onClick={() => {
              this.toggleModal(false, false);
              this.closeFeedbackPanel();
            }}
            isNaked
          >
            Cancel
          </Button>
          <Button
            isPrimary
            data-test-id="feedback--save-button"
            className="mobile:w-full"
            onClick={() => this.sendFeedback(assignmentId, studentId, text)}
            disabled={!hasChanges}
            buttonClassName="save-feedback-button"
            accessibility={{
              'aria-label': 'Save Feedback',
            }}
          >
            Save
          </Button>
        </div>
      </div>,
      document.querySelector('.ScoringByRubric.page_wrapper .ScoringLayout'),
    );
  };

  closeFeedbackPanel = () => {
    const scrollNodes = document.querySelectorAll(
      '[data-scoring-content] .content',
    );
    scrollNodes.forEach((node) =>
      node.classList.remove('--with-feedback-panel'),
    );
  };

  render() {
    const { text: stateText, hasFeedback, isModalOpen } = this.state;

    const {
      assignmentId,
      studentId,
      studentName,
      disabled,
      showFeedbackPanel,
      headerText,
      saveToBrowserKey,
      saveToBrowser,
      hasText,
    } = this.props;

    const parsedText =
      stateText && (isString(stateText) ? JSON.parse(stateText) : stateText);
    const textCursor = makeCursor(parsedText, [], (text) => {
      isModalOpen &&
        saveToBrowser &&
        saveToLocalStorage(saveToBrowserKey, text);
      this.setState({ text });
    });

    const hasChanges =
      stateText &&
      !isEqual(this.props.text && JSON.parse(this.props.text), parsedText);

    return (
      <Fragment>
        <ButtonWithIcon
          background="white"
          className={cn(
            'feedback-button text-blue-700',
            { active: isModalOpen },
            this.props.buttonClassName,
          )}
          icon={hasFeedback ? 'chat_read' : 'chat'}
          iconWidth="15"
          iconHeight="15"
          iconClassName={cn('text-blue-700', {
            'scale-75 transform': hasText,
          })}
          accessibility={{
            'aria-label': `Feedback Button. Feedback panel is ${
              isModalOpen ? 'open' : 'closed'
            }`,
          }}
          disabled={disabled}
          onClick={() => {
            this.toggleModal(true);
            if (showFeedbackPanel) {
              this.moveFocusToFeedbackBox();
            } else {
              this.moveFocusToFeedbackModal();
            }
          }}
        >
          {hasText && 'Feedback'}
        </ButtonWithIcon>
        {this.state.isModalOpen &&
          (showFeedbackPanel ? (
            this.openFeedbackPanel(textCursor, hasChanges)
          ) : (
            <ConfirmDialog
              size="small"
              title={
                headerText ||
                `Provide feedback on this assignment for ${studentName}`
              }
              wrapperClassName="feedback--modal"
              aria={{
                'aria-label':
                  headerText || `Provide feedback for ${studentName}`,
              }}
              isPrimaryBtnDisabled={!hasChanges}
              content={{
                btnPrimary: 'Save',
                btnPrimaryAria: {
                  'data-test-id': 'feedback--save-button',
                },
                btnSecondary: 'Cancel',
              }}
              onClose={() => this.toggleModal(false, true)}
              onSuccess={() =>
                this.sendFeedback(assignmentId, studentId, stateText)
              }
            >
              <ProseMirror valueLink={textCursor} menu="feedback" />
            </ConfirmDialog>
          ))}
      </Fragment>
    );
  }
}

FeedbackButton.propTypes = {
  saveToBrowser: PropTypes.bool,
  saveToBrowserKey: PropTypes.string,
  headerText: PropTypes.string,
  isImpersonating: PropTypes.bool,
};

FeedbackButton.defaultProps = {
  saveToBrowser: false,
  saveToBrowserKey: '',
  isImpersonating: false,
};

export default withApollo(
  graphql(saveFeedback, {
    name: 'saveFeedback',
    options: (props) => ({
      skip: props.isImpersonating,
      refetchQueries: [
        {
          query: assignmentWithAssessment,
          variables: {
            assignmentId: props.assignmentId,
          },
          fetchPolicy: 'network-only',
        },
      ],
    }),
  })(FeedbackButton),
);
